/**
 * Product gallery card + lightbox.
 *
 * The card under Add to Cart previews the gallery as square thumbnails and
 * collapses whatever doesn't fit into a +N chip. Pressing it opens a lightbox
 * that scales out of the card itself, so the image appears to come from the
 * thing you pressed rather than materialising in the middle of the screen.
 */
(function () {
  var FRAME_PAD = 6;
  var OPEN_MS = 420;
  var CLOSE_MS = 340;
  var EASE_IN = 'cubic-bezier(0.22, 1, 0.36, 1)';
  var EASE_OUT = 'cubic-bezier(0.4, 0, 0.2, 1)';

  function reduceMotion() {
    return (
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    );
  }

  /**
   * Hide the thumbnails that overflow and label the remainder.
   *
   * Measured rather than hardcoded: the card is fluid, so the number that fits
   * differs between a phone and a desktop, and between one product and another.
   */
  function layout(card) {
    var track = card.querySelector('[data-wp-gallery-thumbs]');
    var more = card.querySelector('[data-wp-gallery-more]');
    var thumbs = [].slice.call(card.querySelectorAll('[data-wp-gallery-thumb]'));
    if (!track || !thumbs.length) return;

    thumbs.forEach(function (t) {
      t.hidden = false;
    });
    more.hidden = true;
    track.classList.remove('is-packed');

    var styles = window.getComputedStyle(track);
    var gap = parseFloat(styles.columnGap || styles.gap) || 0;
    var available = track.clientWidth;
    if (!available) return;

    var unit = thumbs[0].getBoundingClientRect().width;
    if (!unit) return;

    var fits = Math.floor((available + gap) / (unit + gap));
    if (fits >= thumbs.length) return; // everything fits — no chip needed

    // The chip occupies a slot, so one fewer thumbnail is shown.
    var shown = Math.max(1, fits - 1);
    var hidden = thumbs.length - shown;

    thumbs.forEach(function (t, i) {
      t.hidden = i >= shown;
    });
    more.textContent = '+' + hidden;
    more.hidden = false;

    // Slots rarely divide into the card exactly, and the remainder — up to one
    // thumbnail wide — piles up after the chip and reads as a missing image.
    // Push it into the gaps instead so the row runs edge to edge. Only when
    // packed: with two or three thumbs, spreading them apart would look worse.
    track.classList.add('is-packed');
  }

  function buildLightbox() {
    var root = document.createElement('div');
    root.className = 'wp-lightbox';
    root.setAttribute('role', 'dialog');
    root.setAttribute('aria-modal', 'true');
    root.setAttribute('aria-label', 'Product gallery');
    root.hidden = true;
    root.innerHTML =
      '<button type="button" class="wp-lightbox__backdrop" aria-label="Close gallery"></button>' +
      '<figure class="wp-lightbox__frame"><img class="wp-lightbox__img" alt=""></figure>' +
      '<button type="button" class="wp-lightbox__nav wp-lightbox__nav--prev" aria-label="Previous image">&#8592;</button>' +
      '<button type="button" class="wp-lightbox__nav wp-lightbox__nav--next" aria-label="Next image">&#8594;</button>' +
      '<p class="wp-lightbox__counter" aria-live="polite"></p>' +
      '<button type="button" class="wp-lightbox__close" aria-label="Close gallery">&#215;</button>';
    document.body.appendChild(root);
    return root;
  }

  function init(card) {
    var images = [].slice.call(card.querySelectorAll('img')).map(function (img) {
      return { src: img.getAttribute('data-full'), alt: img.getAttribute('data-alt') || '' };
    });
    if (!images.length) return;

    var box = null;
    var index = 0;
    var lastFocus = null;
    var closeTimer = 0;

    function els() {
      return {
        frame: box.querySelector('.wp-lightbox__frame'),
        img: box.querySelector('.wp-lightbox__img'),
        counter: box.querySelector('.wp-lightbox__counter'),
        prev: box.querySelector('.wp-lightbox__nav--prev'),
        next: box.querySelector('.wp-lightbox__nav--next')
      };
    }

    function show(i) {
      var e = els();
      index = (i + images.length) % images.length;
      e.img.src = images[index].src;
      e.img.alt = images[index].alt;
      e.counter.textContent = index + 1 + ' / ' + images.length;
      var single = images.length < 2;
      e.prev.hidden = single;
      e.next.hidden = single;
    }

    /** Scale the frame down onto the card, then release it — a FLIP transition. */
    function animateFrom(frame, rect, opening) {
      var to = frame.getBoundingClientRect();
      if (!to.width || !to.height) return;
      var dx = rect.left + rect.width / 2 - (to.left + to.width / 2);
      var dy = rect.top + rect.height / 2 - (to.top + to.height / 2);
      var scale = Math.max(0.08, Math.min(rect.width / to.width, rect.height / to.height));
      var from = 'translate(' + dx + 'px, ' + dy + 'px) scale(' + scale + ')';

      if (opening) {
        frame.style.transition = 'none';
        frame.style.transform = from;
        frame.style.opacity = '0';
        void frame.offsetWidth; // flush, so the transition has a start value
        frame.style.transition =
          'transform ' + OPEN_MS + 'ms ' + EASE_IN + ', opacity ' + OPEN_MS + 'ms ' + EASE_IN;
        frame.style.transform = 'none';
        frame.style.opacity = '1';
      } else {
        frame.style.transition =
          'transform ' + CLOSE_MS + 'ms ' + EASE_OUT + ', opacity ' + CLOSE_MS + 'ms ' + EASE_OUT;
        frame.style.transform = from;
        frame.style.opacity = '0';
      }
    }

    function close() {
      if (!box || box.hidden) return;
      var frame = box.querySelector('.wp-lightbox__frame');
      var done = function () {
        box.hidden = true;
        box.classList.remove('is-open');
        document.documentElement.style.overflow = '';
        frame.style.cssText = '';
        // Fall back to the card: Safari doesn't focus buttons on click, so
        // lastFocus can be <body>, and returning there strands the keyboard.
        var restore =
          lastFocus && lastFocus.focus && lastFocus !== document.body ? lastFocus : card;
        restore.focus();
      };

      if (reduceMotion()) return done();
      box.classList.remove('is-open');
      animateFrom(frame, card.getBoundingClientRect(), false);
      window.clearTimeout(closeTimer);
      closeTimer = window.setTimeout(done, CLOSE_MS);
    }

    function open() {
      if (!box) {
        box = buildLightbox();
        box.querySelector('.wp-lightbox__backdrop').addEventListener('click', close);
        box.querySelector('.wp-lightbox__close').addEventListener('click', close);
        box.querySelector('.wp-lightbox__nav--prev').addEventListener('click', function () {
          show(index - 1);
        });
        box.querySelector('.wp-lightbox__nav--next').addEventListener('click', function () {
          show(index + 1);
        });
      }

      window.clearTimeout(closeTimer);
      lastFocus = document.activeElement;
      box.hidden = false;
      show(0);
      // Lock the page behind the dialog, not the dialog itself.
      document.documentElement.style.overflow = 'hidden';

      var frame = box.querySelector('.wp-lightbox__frame');
      frame.style.cssText = '';
      if (reduceMotion()) {
        box.classList.add('is-open');
      } else {
        animateFrom(frame, card.getBoundingClientRect(), true);
        requestAnimationFrame(function () {
          box.classList.add('is-open');
        });
      }
      box.querySelector('.wp-lightbox__close').focus();
    }

    card.addEventListener('click', open);

    document.addEventListener('keydown', function (evt) {
      if (!box || box.hidden) return;
      if (evt.key === 'Escape') {
        close();
      } else if (evt.key === 'ArrowRight') {
        show(index + 1);
      } else if (evt.key === 'ArrowLeft') {
        show(index - 1);
      }
    });

    layout(card);

    var resizeTimer = 0;
    window.addEventListener('resize', function () {
      window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(function () {
        layout(card);
      }, 120);
    });

    // Thumbs are lazy-loaded, so their width can settle after first paint.
    window.addEventListener('load', function () {
      layout(card);
    });
  }

  function boot() {
    [].slice.call(document.querySelectorAll('[data-wp-gallery]')).forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
